/**
 * Shared entry-status humanization for the tournaments module.
 *
 * A draw entry's `status` arrives as a raw backend enum (e.g. `partner_wanted`,
 * `pending_review`, `withdrawn`). The UI must show READABLE copy a player
 * understands — never the raw lowercase/underscored token — while staying an
 * HONEST mirror of the server's verbatim status (no client re-derivation of
 * state). An unmapped/unknown status is surfaced verbatim (underscores → spaces,
 * Title-cased) rather than hidden or coerced to a friendly known label — so the
 * player always sees exactly the truth the server recorded (a `disqualified`
 * entry reads "Disqualified", never a fabricated "Registered").
 *
 * Single source of truth: imported by BOTH `TournamentEventRow` (the multi-event
 * register surface) and `MyEntriesPage` (the cross-tournament history list) so the
 * two surfaces read the SAME copy for the same status with no duplicated map.
 */

/**
 * Pinned labels for the common statuses, so the copy is intentional rather than a
 * mechanical capitalize. `confirmed` reads as "Registered" (the same player-facing
 * outcome). Anything not pinned falls through to {@link humanizeEntryStatus}'s
 * verbatim capitalize.
 */
export const STATUS_LABELS: Record<string, string> = {
  registered: "Registered",
  confirmed: "Registered",
  waitlisted: "Waitlisted",
  pending_review: "Pending review",
  partner_wanted: "Needs a partner",
  // A MINOR's entry is held by the backend until their parent/guardian clicks the
  // emailed consent link (#1684). The player sees this honest "awaiting consent"
  // label on every entry surface (the register confirmation, the per-event row,
  // and the cross-tournament "My entries" list) — never the raw enum token.
  pending_guardian_consent: "Pending parental consent",
};

/**
 * Map a raw entry-status enum → readable copy. Pinned labels win; otherwise the
 * token is shown verbatim with underscores turned to spaces and the first letter
 * capitalized (so `pending` → "Pending", `disqualified` → "Disqualified"). An
 * empty/falsy status degrades to an em-dash rather than a blank.
 */
export function humanizeEntryStatus(status: string): string {
  const pinned = STATUS_LABELS[status];
  if (pinned) return pinned;
  if (!status) return "—";
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
